import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SECTORS = [
  { label: 'N', min: 337.5, max: 360, points: 80 },
  { label: 'N', min: 0, max: 22.5, points: 80 },
  { label: 'NE', min: 22.5, max: 67.5, points: 50 },
  { label: 'E', min: 67.5, max: 112.5, points: 60 },
  { label: 'SE', min: 112.5, max: 157.5, points: 50 },
  { label: 'S', min: 157.5, max: 202.5, points: 80 },
  { label: 'SW', min: 202.5, max: 247.5, points: 50 },
  { label: 'W', min: 247.5, max: 292.5, points: 60 },
  { label: 'NW', min: 292.5, max: 337.5, points: 50 },
];

const TARGETS = [
  { label: 'N', color: '#c9a84c', points: 120, desc: 'True North — hardest target' },
  { label: 'E', color: '#a8cfe0', points: 80, desc: 'East — medium target' },
  { label: 'S', color: '#7fa8c0', points: 80, desc: 'South — medium target' },
  { label: 'W', color: '#a8cfe0', points: 80, desc: 'West — medium target' },
  { label: 'NE', color: '#6b8fa8', points: 50, desc: 'Northeast — wide sector' },
  { label: 'NW', color: '#6b8fa8', points: 50, desc: 'Northwest — wide sector' },
  { label: 'SE', color: '#6b8fa8', points: 50, desc: 'Southeast — wide sector' },
  { label: 'SW', color: '#6b8fa8', points: 50, desc: 'Southwest — wide sector' },
];

function randomTarget() {
  return TARGETS[Math.floor(Math.random() * TARGETS.length)];
}

export default function CompassRead() {
  const [angle, setAngle] = useState(0);
  const [speed, setSpeed] = useState(1.4);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [round, setRound] = useState(1);
  const [target, setTarget] = useState(() => randomTarget());
  const [result, setResult] = useState(null); // {hit, earned, label}
  const [spinning, setSpinning] = useState(true);
  const [history, setHistory] = useState([]);
  const animRef = useRef(null);
  const angleRef = useRef(0);
  const dirRef = useRef(1);

  // Oscillate the compass needle
  useEffect(() => {
    if (!spinning) return;
    let last = null;
    const step = (ts) => {
      if (last !== null) {
        const delta = ts - last;
        angleRef.current += dirRef.current * speed * delta * 0.06;
        if (angleRef.current >= 359) { dirRef.current = -1; }
        if (angleRef.current <= 0) { dirRef.current = 1; }
        setAngle(angleRef.current % 360);
      }
      last = ts;
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [spinning, speed]);

  const handleStop = useCallback(() => {
    if (!spinning || result) return;
    setSpinning(false);
    cancelAnimationFrame(animRef.current);

    const a = angleRef.current % 360;
    let hit = false;
    let earned = 0;

    for (const s of SECTORS) {
      const inRange = s.min === 337.5
        ? a >= 337.5 || a < 22.5
        : a >= s.min && a < s.max;
      if (inRange && s.label === target.label) {
        hit = true;
        earned = target.points;
        break;
      }
    }

    setResult({ hit, earned, label: target.label });
    setHistory(h => [...h, { round, target: target.label, hit, earned }].slice(-8));
    if (hit) setSessionPoints(p => p + earned);
  }, [spinning, result, target, round]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => { if (e.code === 'Space') handleStop(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleStop]);

  const nextRound = () => {
    setResult(null);
    setTarget(randomTarget());
    setRound(r => r + 1);
    setSpeed(s => Math.min(s + 0.08, 3.5));
    angleRef.current = Math.random() * 360;
    setSpinning(true);
  };

  const reset = () => {
    setSessionPoints(0);
    setRound(1);
    setResult(null);
    setTarget(randomTarget());
    setHistory([]);
    setSpeed(1.4);
    angleRef.current = 0;
    setSpinning(true);
  };

  const rad = (angle * Math.PI) / 180;
  const cx = 150, cy = 150;

  return (
    <div style={{ minHeight: '100vh', padding: '40px 0 80px' }}>
      <div className="container" style={{ maxWidth: '900px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '32px', fontSize: '0.82rem', color: 'rgba(168,207,224,0.4)' }}>
          <Link to="/" style={{ color: 'rgba(168,207,224,0.4)' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <Link to="/activities" style={{ color: 'rgba(168,207,224,0.4)' }}>Activities</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: 'rgba(168,207,224,0.7)' }}>Compass Read</span>
        </div>

        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <div className="label label-amber" style={{ marginBottom: '12px' }}>Timing · Precision</div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginBottom: '12px' }}>Compass Read</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', fontSize: '1.2rem', marginBottom: '12px' }}>Stop the needle in the target sector</h2>
          <p style={{ maxWidth: '500px' }}>
            Stop the compass needle in the target sector. Press the button or hit Space at the right moment. Session points only — no monetary value.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="game-layout">

          {/* Compass */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>

            {/* Target display */}
            <AnimatePresence mode="wait">
              <motion.div
                key={target.label}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                style={{
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  borderRadius: '12px',
                  padding: '14px 28px',
                  textAlign: 'center',
                  minWidth: '220px',
                }}
              >
                <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Navigate to</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '2rem', fontWeight: 800, color: '#c9a84c' }}>{target.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(168,207,224,0.5)', marginTop: '2px' }}>{target.desc}</div>
              </motion.div>
            </AnimatePresence>

            {/* SVG Compass */}
            <svg
              viewBox="0 0 300 300"
              style={{ width: '280px', height: '280px', cursor: spinning ? 'pointer' : 'default', userSelect: 'none' }}
              onClick={handleStop}
              role="button"
              aria-label="Stop compass needle"
            >
              {/* Background */}
              <circle cx={cx} cy={cy} r="140" fill="rgba(13,31,53,0.8)" stroke="rgba(168,207,224,0.15)" strokeWidth="1.5" />

              {/* Sector highlights */}
              {SECTORS.filter(s => s.label === target.label).map((s, i) => {
                const startRad = ((s.min - 90) * Math.PI) / 180;
                const endRad = ((s.max - 90) * Math.PI) / 180;
                const r = 125;
                const x1 = cx + r * Math.cos(startRad), y1 = cy + r * Math.sin(startRad);
                const x2 = cx + r * Math.cos(endRad), y2 = cy + r * Math.sin(endRad);
                const large = s.max - s.min > 180 ? 1 : 0;
                return (
                  <path key={i}
                    d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
                    fill="rgba(201,168,76,0.1)" stroke="rgba(201,168,76,0.3)" strokeWidth="1"
                  />
                );
              })}

              {/* Degree marks */}
              {Array.from({ length: 72 }, (_, i) => {
                const a = (i * 5 * Math.PI) / 180;
                const r1 = 133, r2 = i % 9 === 0 ? 118 : i % 3 === 0 ? 124 : 128;
                return (
                  <line key={i}
                    x1={cx + r1 * Math.sin(a)} y1={cy - r1 * Math.cos(a)}
                    x2={cx + r2 * Math.sin(a)} y2={cy - r2 * Math.cos(a)}
                    stroke={i % 9 === 0 ? 'rgba(201,168,76,0.5)' : 'rgba(168,207,224,0.15)'}
                    strokeWidth={i % 9 === 0 ? 1.5 : 0.8}
                  />
                );
              })}

              {/* Cardinal labels */}
              {[['N', 150, 14, '#c9a84c'], ['E', 290, 155, 'rgba(168,207,224,0.6)'], ['S', 150, 293, 'rgba(168,207,224,0.6)'], ['W', 10, 155, 'rgba(168,207,224,0.6)']].map(([l, x, y, c]) => (
                <text key={l} x={x} y={y} textAnchor="middle" dominantBaseline="middle" fontSize="11" fontFamily="Syne, sans-serif" fontWeight="700" fill={c}>{l}</text>
              ))}

              {/* Needle */}
              <g transform={`rotate(${angle}, ${cx}, ${cy})`}>
                <polygon points={`${cx},${cy - 110} ${cx + 7},${cy} ${cx},${cy + 15} ${cx - 7},${cy}`} fill="#c9a84c" />
                <polygon points={`${cx},${cy + 15} ${cx + 7},${cy} ${cx},${cy + 110} ${cx - 7},${cy}`} fill="rgba(168,207,224,0.3)" />
              </g>

              {/* Center */}
              <circle cx={cx} cy={cy} r="8" fill="#0d1f35" stroke="rgba(201,168,76,0.6)" strokeWidth="1.5" />
              <circle cx={cx} cy={cy} r="3" fill="#c9a84c" />

              {/* Hit overlay */}
              {result && (
                <circle cx={cx} cy={cy} r="140"
                  fill={result.hit ? 'rgba(100,200,100,0.06)' : 'rgba(200,80,80,0.06)'}
                  stroke={result.hit ? 'rgba(100,200,100,0.3)' : 'rgba(200,80,80,0.3)'}
                  strokeWidth="2"
                />
              )}
            </svg>

            {/* Stop button */}
            {spinning && !result && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={handleStop}
                style={{
                  padding: '14px 44px',
                  background: '#c9a84c', color: '#0d1f35',
                  border: 'none', borderRadius: '999px',
                  fontFamily: 'Syne, sans-serif', fontWeight: 800,
                  fontSize: '0.95rem', cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}
              >
                STOP NEEDLE
              </motion.button>
            )}

            {/* Result */}
            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    textAlign: 'center',
                    background: result.hit ? 'rgba(100,200,100,0.08)' : 'rgba(200,80,80,0.08)',
                    border: `1px solid ${result.hit ? 'rgba(100,200,100,0.25)' : 'rgba(200,80,80,0.2)'}`,
                    borderRadius: '16px', padding: '20px 32px',
                    minWidth: '220px',
                  }}
                >
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: result.hit ? '#80c880' : '#e07070', marginBottom: '6px' }}>
                    {result.hit ? `+${result.earned} pts` : 'Off course'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'rgba(168,207,224,0.5)', marginBottom: '16px' }}>
                    {result.hit ? `Landed on ${result.label}` : `Missed ${result.label} — try again`}
                  </div>
                  <button onClick={nextRound} className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '10px 24px' }}>
                    Next heading →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar: score + history */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Score */}
            <div className="card">
              <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Session Points</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '2.8rem', fontWeight: 800, color: '#c9a84c', lineHeight: 1 }}>{sessionPoints}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(168,207,224,0.35)', marginTop: '6px' }}>No monetary value · Resets on close</div>
            </div>

            {/* Round */}
            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Round</div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>{round}</div>
                </div>
                <button onClick={reset} style={{ fontSize: '0.75rem', color: 'rgba(168,207,224,0.4)', background: 'none', border: '1px solid rgba(168,207,224,0.15)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer' }}>
                  Reset
                </button>
              </div>
            </div>

            {/* History */}
            {history.length > 0 && (
              <div className="card">
                <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>Recent Headings</div>
                {history.slice().reverse().map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < history.length - 1 ? '1px solid rgba(168,207,224,0.06)' : 'none', fontSize: '0.82rem' }}>
                    <span style={{ color: 'rgba(168,207,224,0.6)' }}>Round {h.round} — {h.target}</span>
                    <span style={{ color: h.hit ? '#80c880' : '#e07070', fontWeight: 600 }}>{h.hit ? `+${h.earned}` : '—'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <div style={{ padding: '14px', background: 'rgba(168,207,224,0.03)', border: '1px solid rgba(168,207,224,0.08)', borderRadius: '10px', fontSize: '0.75rem', color: 'rgba(168,207,224,0.35)', lineHeight: 1.65 }}>
              Session points have no monetary value and cannot be redeemed, purchased, or exchanged. This is a free entertainment activity. Adults 18+ only.
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(min-width:768px){.game-layout{grid-template-columns:1fr 280px !important;}}`}</style>
    </div>
  );
}
